import { groupBy } from 'underscore';
import dayjs from 'dayjs';

/**
 * Convierte fecha UTC a formato local
 * @param {string} utcDateStr - Fecha en formato UTC
 * @returns {Date} Fecha convertida a local
 */
const convertirUTCaLocal = (utcDateStr) => {
  if (!utcDateStr) return null;
  
  // Crear fecha a partir de string UTC
  const fecha = new Date(utcDateStr);
  
  // Formatear en el formato deseado
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  const hours = String(fecha.getHours()).padStart(2, '0');
  const minutes = String(fecha.getMinutes()).padStart(2, '0');
  const seconds = String(fecha.getSeconds()).padStart(2, '0');
  
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Obtiene el nombre del día de la semana en español
 * @param {Date} fecha - Objeto fecha
 * @returns {string} Nombre del día en español
 */
const obtenerNombreDia = (fecha) => {
  const dia = new Date(fecha).getDay();
  const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return diasSemana[dia];
};

/**
 * Procesa los datos del API para generar el reporte de kilometraje
 * @param {Array} datos - Datos crudos del API
 * @param {Object} filtros - Filtros aplicados (distancia_1, distancia_2)
 * @returns {Array} Datos procesados en formato esperado
 */

// Modificar la función procesarDatosKilometraje para consolidar registros por día
export const procesarDatosKilometraje = (datos, filtros) => {
  if (!datos || datos.length === 0) {
    console.log("[KM] No hay datos para procesar");
    return [];
  }
  
  console.log(`[KM] Total puntos recibidos: ${datos.length}`);
  
  // Extrae valores de filtros
  const distanciaMin = filtros?.distancia_1 || 0;
  const distanciaMax = filtros?.distancia_2 || 0;
  
  // Paso 1: Convertir fechas UTC a local
  const datosConFechasLocales = datos.map(item => ({
    ...item,
    fechaLocal: convertirUTCaLocal(item.fecha),
    fechaObj: new Date(item.fecha)
  }));
  
  // Paso 2: Agrupar por vehículo
  const agrupadoPorVehiculo = groupBy(datosConFechasLocales, 'vehiculoId');
  //console.log(`[KM] Vehículos encontrados: ${Object.keys(agrupadoPorVehiculo).length}`);
  
  let resultado = [];
  let indice = 1;
  let totalPuntosOriginales = 0;
  let totalDiasConsolidados = 0;
  
  // Paso 3: Procesar cada grupo de vehículo
  Object.keys(agrupadoPorVehiculo).forEach(vehiculoId => {
    const puntosVehiculo = agrupadoPorVehiculo[vehiculoId];
    totalPuntosOriginales += puntosVehiculo.length;
    
    //console.log(`[KM] Vehículo ${vehiculoId}: ${puntosVehiculo.length} puntos`);
    
    // Obtener información del vehículo del primer punto
    const primerPunto = puntosVehiculo[0];
    
    // Paso 4: Agrupar por día del año
    const puntosOrdenados = puntosVehiculo.sort((a, b) => a.fechaObj - b.fechaObj);
    
    // Agrupar por fecha (YYYY/MM/DD)
    const agrupadoPorDia = groupBy(puntosOrdenados, item => 
      dayjs(item.fechaObj).format('YYYY/MM/DD')
    );
    
    //console.log(`[KM] Vehículo ${vehiculoId}: Días encontrados: ${Object.keys(agrupadoPorDia).length}`);
    
    // Paso 5: Consolidar registros para cada día
    Object.keys(agrupadoPorDia).forEach(fecha => {
      const puntosDia = agrupadoPorDia[fecha];
      
      // Calcular distancia total sumando todos los puntos del día
      const distanciaTotal = puntosDia.reduce((total, punto) => 
        total + (punto.distancia || 0), 0);
      
      // Filtrar por distancia si es necesario
      if ((distanciaMin > 0 && distanciaTotal < distanciaMin) || 
          (distanciaMax > 0 && distanciaTotal > distanciaMax)) {
        console.log(`[KM] Omitiendo día ${fecha} - Distancia ${distanciaTotal} fuera de rango (${distanciaMin}-${distanciaMax})`);
        return; // Omitir este día
      }
      
      totalDiasConsolidados++;
      console.log(`[KM] Procesando día ${fecha}: ${puntosDia.length} puntos, distancia: ${distanciaTotal.toFixed(2)}km`);
      
       // Filtrar puntos para el mapa (excluyendo apagados)
      const puntosParaMapa = puntosDia
        .filter(punto => !((punto.tipo === 1 || punto.tipo === 2) && punto.tipoMotor === 0))
        .map(punto => ({
          fecha: dayjs(punto.fechaObj).format('YYYY/MM/DD hh:mm A'),
          evento: punto.nombreEvento,
          latitud: punto.latitud?.toString() || '0',
          longitud: punto.longitud?.toString() || '0',
          distancia: punto.distancia || 0,
          tipoEvento: punto.tipo,
          tipoMotor: punto.tipoMotor,
          velocidad: punto.velocidad || 0
        }))
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        
      // Crear un único objeto resultado para este día
      resultado.push({
        recorridoId: indice++,
        vehiculoId: parseInt(vehiculoId),
        vehiculoUid: primerPunto.vehiculos?.vehiculoUid || primerPunto.vehiculoUid,
        promedio_distancia: Math.round(distanciaTotal * 100) / 100, // Distancia total del día
        cliente: `${obtenerNombreDia(puntosDia[0].fechaObj)} - ${dayjs(puntosDia[0].fechaObj).format('DD/MM/YYYY')}`,
        listPoints: puntosParaMapa,
        noEconomico: primerPunto.vehiculos?.noEconomico || 'N/A',
        aliasVehiculo: primerPunto.vehiculos?.alias || 'N/A',
        imei: primerPunto.dispositivoId || 'N/A',
        mapa: "", // Se maneja en el frontend
        fecha: new Date(puntosDia[0].fechaObj),
        fechaCorta: dayjs(puntosDia[0].fechaObj).format('YYYY/MM/DD')
      });
    });
  });
  
  // console.log(`[KM] RESUMEN DEL PROCESAMIENTO:`);
  // console.log(`[KM] - Total puntos originales: ${totalPuntosOriginales}`);
  // console.log(`[KM] - Total días procesados: ${totalDiasConsolidados}`);
  // console.log(`[KM] - Total registros resultantes: ${resultado.length}`);
  // console.log(`[KM] - Tasa de consolidación: ${(totalPuntosOriginales/resultado.length).toFixed(2)} puntos por registro`);
  
  return resultado;
};

/**
 * Transforma la respuesta del endpoint /distance al formato
 * que consumen los componentes del reporte KM.
 *
 * El endpoint /distance retorna datos ya agregados por vehículo y día
 * desde MongoDB (aggregation pipeline), eliminando la necesidad de
 * descargar millones de registros GPS crudos.
 *
 * @param {Array} vehiculosData - response.data.data.vehiculos del endpoint /distance
 * @param {Object} filtros - filtros con distancia_1 y distancia_2
 * @returns {Array} - mismo formato que procesarDatosKilometraje()
 */
export const transformarRespuestaDistance = (vehiculosData, filtros) => {
  const resultado = [];
  let indice = 0;

  const distanciaMin = parseFloat(filtros?.distancia_1) || 0;
  const distanciaMax = parseFloat(filtros?.distancia_2) || 0;

  if (!Array.isArray(vehiculosData) || vehiculosData.length === 0) {
    console.log("[KM-Distance] No hay datos de vehículos para transformar");
    return resultado;
  }

  vehiculosData.forEach(vehiculo => {
    if (!vehiculo.dias || !Array.isArray(vehiculo.dias)) return;

    vehiculo.dias.forEach(dia => {
      const distanciaDia = dia.distancia || 0;

      // Aplicar filtros de distancia (misma lógica que procesarDatosKilometraje)
      if (distanciaMin > 0 && distanciaDia < distanciaMin) return;
      if (distanciaMax > 0 && distanciaDia > distanciaMax) return;

      // dia.fecha viene como "2026-04-08" (YYYY-MM-DD)
      const fechaObj = dayjs(dia.fecha);

      resultado.push({
        recorridoId: indice++,
        vehiculoId: vehiculo.vehiculoId,
        vehiculoUid: vehiculo.vehiculoUid,
        promedio_distancia: Math.round(distanciaDia * 100) / 100,
        cliente: `${obtenerNombreDia(fechaObj)} - ${fechaObj.format('DD/MM/YYYY')}`,
        listPoints: [], // vacío — carga lazy on-demand al hacer clic en "Ver Mapa"
        noEconomico: vehiculo.noEconomico || 'N/A',
        aliasVehiculo: vehiculo.alias || 'N/A',
        imei: vehiculo.dispositivoId || 'N/A',
        mapa: '',
        fecha: fechaObj.format('YYYY/MM/DD HH:mm:ss'),
        fechaCorta: fechaObj.format('YYYY/MM/DD'),
        // Campos adicionales del endpoint /distance
        velocidadMaxima: dia.velocidadMaxima || 0,
        velocidadPromedio: dia.velocidadPromedio || 0,
        totalPuntos: dia.totalPuntos || 0,
        odometroInicial: dia.odometroInicial || 0,
        odometroFinal: dia.odometroFinal || 0
      });
    });
  });

  console.log(`[KM-Distance] Transformación completada: ${vehiculosData.length} vehículos → ${resultado.length} registros`);

  return resultado;
};


// export const procesarDatosKilometraje = (datos, filtros) => {
//   if (!datos || datos.length === 0) {
//     return [];
//   }
  
//   // Extrae valores de filtros
//   const distanciaMin = filtros?.distancia_1 || 0;
//   const distanciaMax = filtros?.distancia_2 || 0;
  
//   // Paso 1: Convertir fechas UTC a local
//   const datosConFechasLocales = datos.map(item => ({
//     ...item,
//     fechaLocal: convertirUTCaLocal(item.fecha),
//     fechaObj: new Date(item.fecha)
//   }));
  
//   // Paso 2: Agrupar por vehículo
//   const agrupadoPorVehiculo = groupBy(datosConFechasLocales, 'vehiculoId');
  
//   let resultado = [];
//   let indice = 1;
  
//   // Paso 3: Procesar cada grupo de vehículo
//   Object.keys(agrupadoPorVehiculo).forEach(vehiculoId => {
//     const puntosVehiculo = agrupadoPorVehiculo[vehiculoId];
    
//     // Obtener información del vehículo del primer punto
//     const primerPunto = puntosVehiculo[0];
    
//     // Paso 4: Agrupar por día del año
//     const puntosOrdenados = puntosVehiculo.sort((a, b) => a.fechaObj - b.fechaObj);
//     const agrupadoPorDia = groupBy(puntosOrdenados, item => 
//       new Date(item.fechaObj).toISOString().split('T')[0]
//     );
    
//     // Paso 5: Procesar cada día
//     Object.keys(agrupadoPorDia).forEach(fecha => {
//       const puntosDia = agrupadoPorDia[fecha];
      
//       // Calcular distancia total
//       const distanciaTotal = puntosDia.reduce((total, punto) => total + (punto.distancia || 0), 0);
      
//       // Filtrar por distancia si es necesario
//       if ((distanciaMin > 0 && distanciaTotal < distanciaMin) || 
//           (distanciaMax > 0 && distanciaTotal > distanciaMax)) {
//         return; // Skip this day
//       }
      
//       // Filtrar puntos para el mapa (excluyendo apagados)
//       const puntosParaMapa = puntosDia
//         .filter(punto => !((punto.tipo === 1 || punto.tipo === 2) && punto.tipoMotor === 0))
//         .map(punto => ({
//           fecha: dayjs(punto.fechaObj).format('YYYY/MM/DD hh:mm A'),
//           evento: punto.nombreEvento,
//           latitud: punto.latitud?.toString() || '0',
//           longitud: punto.longitud?.toString() || '0',
//           distancia: punto.distancia || 0,
//           tipoEvento: punto.tipo,
//           tipoMotor: punto.tipoMotor,
//           velocidad: punto.velocidad || 0
//         }))
//         .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      
//       // Crear objeto resultado
//       const recorridoDia = {
//         recorridoId: indice++,
//         vehiculoId: parseInt(vehiculoId),
//         vehiculoUid: primerPunto.vehiculos?.vehiculoUid || primerPunto.vehiculoUid,
//         promedio_distancia: Math.round(distanciaTotal * 100) / 100,
//         cliente: obtenerNombreDia(puntosDia[0].fechaObj),
//         listPoints: puntosParaMapa,
//         noEconomico: primerPunto.vehiculos?.noEconomico || 'N/A',
//         aliasVehiculo: primerPunto.vehiculos?.alias || 'N/A',
//         imei: primerPunto.dispositivoId || 'N/A',
//         mapa: "", // Se maneja en el frontend
//         fecha: new Date(puntosDia[0].fechaObj),
//         fechaCorta: dayjs(puntosDia[0].fechaObj).format('YYYY/MM/DD')
//       };
      
//       resultado.push(recorridoDia);
//     });
//   });
  
//   return resultado;
// };